import { UserAdhesionDetailsI } from '../api/users/interfaces/UserAdhesionDetailsI';
import { PensionPlanTransfer } from '../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import { formatArcaNumberCalculeCents } from './formatArcaNumbers';

/**
 * Performance calculation result
 */
export interface PerformanceResult {
	percentage: number;
	amount: number;
}

/**
 * User type for pension plan calculations
 */
export enum UserType {
	COMPANY = 'COMPANY', // Usuario de empresa con plan de pensiones de empleo
	INDIVIDUAL = 'INDIVIDUAL' // Usuario individual con traspaso de plan externo
}

/**
 * Tipo de plan al que pertenece un traspaso (espejo de `pension_plan_registry.plan_type`
 * proyectado en `transfer.catalog.typology` desde backend).
 *
 * Sirve para discriminar transfers en usuarios MIXTOS (B2B + plan individual) y
 * evitar que el traspaso individual contamine los cálculos de la vista Empleado
 * (y viceversa).
 */
export type PlanTypology = 'EMPLEO' | 'INDIVIDUAL' | 'AUTONOMOS';

/**
 * Input parameters for unified pension plan calculations
 */
export interface PensionPlanMetricsInput {
	adhesionDetails: UserAdhesionDetailsI[];
	userType: UserType;
	transfers?: PensionPlanTransfer[];
	/**
	 * Si se proporciona, los cálculos dependientes de `transfers` solo consideran
	 * los traspasos cuyo `catalog.typology` coincide (los huérfanos sin catalog
	 * se asumen EMPLEO por fallback histórico — la mayoría de transfers
	 * pre-typology eran de plan empleo).
	 */
	planTypology?: PlanTypology;
	/**
	 * Base de coste de traspasos externos (€): `Σ TRANSFER_EXTERNAL_INPUT` confirmados,
	 * que llega del endpoint `user-cost-basis` (back). En planes traspasados,
	 * `daily.total_contributions` es 0 (o no incluye el traspaso) → el rendimiento sale
	 * `+0,00%` (traspaso puro) o inflado (traspaso + aportaciones). Si se proporciona,
	 * se SUMA a la base de coste (always-add). Si se omite (0), el cálculo es idéntico
	 * al histórico (sin regresión).
	 */
	externalTransferCostBasis?: number;
	/**
	 * Total rescatado (€): `Σ CANCELLMENT_PARTICIPANT_SAVINGS`. Se suma de vuelta al
	 * valor para no penalizar el rendimiento por capital ya dispuesto. Opcional.
	 */
	totalRescued?: number;
}

/**
 * Filtra una lista de traspasos por tipología de plan. Cuando `planTypology`
 * se omite, devuelve el array original (backward compat). Transfers sin
 * `catalog.typology` se asimilan a EMPLEO (fallback legacy).
 */
export function filterTransfersByPlanTypology(
	transfers: PensionPlanTransfer[],
	planTypology?: PlanTypology
): PensionPlanTransfer[] {
	if (!transfers || transfers.length === 0) return [];
	if (!planTypology) return transfers;
	return transfers.filter((transfer) => (transfer.catalog?.typology || 'EMPLEO') === planTypology);
}

/**
 * Complete result of pension plan metrics calculation
 */
export interface PensionPlanMetricsResult {
	// Basic metrics
	totalSaved: number;
	totalContributions: number;
	totalTransferred: number;

	// Contribution breakdown
	sumVestedRights: number;
	sumTotalContributions: number;
	sumCompanyContributions: number;
	sumUserContributions: number;
	formattedCompanyContributions: number;
	formattedUserContributions: number;

	// Products
	actionsProduct: UserAdhesionDetailsI | undefined;
	bondsProduct: UserAdhesionDetailsI | undefined;

	// Performance
	performance: PerformanceResult;
	isPerformanceReliable: boolean;

	// ROI and benefit (mainly for company users)
	roi: number;
	/**
	 * `true` cuando el ROI NO es calculable: el plan tiene un traspaso externo
	 * (`externalTransferCostBasis > 0`) y por tanto la "aportación propia del empleado"
	 * es desconocida (ocurrió fuera de Arca). La UI debe mostrar "Sin datos suficientes"
	 * + (i) en lugar de un porcentaje. El rendimiento del fondo SÍ se calcula.
	 */
	roiIndeterminate: boolean;
	totalBenefitInEuros: number;
}

/**
 * Sums a specific numeric field from an array of adhesion details
 * @param adhesionDetails - Array of user adhesion details
 * @param field - Field name to sum
 * @returns Sum of the field values
 */
export function sumAdhesionDetailsField(
	adhesionDetails: UserAdhesionDetailsI[],
	field: keyof Pick<
		UserAdhesionDetailsI,
		'vestedRights' | 'totalContributions' | 'companyContributions' | 'userContributions'
	>
): number {
	if (!adhesionDetails || adhesionDetails.length === 0) {
		return 0;
	}

	return adhesionDetails.reduce((acc, item) => acc + (item[field] as number), 0);
}

/**
 * Gets a product from adhesion details by product code
 * If only one product exists, returns it regardless of code
 * @param adhesionDetails - Array of user adhesion details
 * @param productCode - Product code to search for (31049 for actions, 31050 for bonds)
 * @returns Matching adhesion detail or undefined
 */
export function getProductByCode(
	adhesionDetails: UserAdhesionDetailsI[],
	productCode: number
): UserAdhesionDetailsI | undefined {
	if (!adhesionDetails || adhesionDetails.length === 0) {
		return undefined;
	}

	if (adhesionDetails.length === 1) {
		return adhesionDetails[0];
	}

	return adhesionDetails.find((item) => item.productCode === productCode);
}

/**
 * Calculates the total saved amount
 * Uses vestedRights if available, otherwise calculates from participations
 * @param adhesionDetails - Array of user adhesion details
 * @param sumVestedRights - Sum of vested rights from all products
 * @returns Total saved amount in euros
 */
export function calculateTotalSaved(
	adhesionDetails: UserAdhesionDetailsI[],
	sumVestedRights: number
): number {
	if (!adhesionDetails || adhesionDetails.length === 0) {
		return 0;
	}

	// If vested rights is available, use it (convert from cents to euros)
	if (sumVestedRights > 0) {
		return sumVestedRights / 100;
	}

	// Otherwise, calculate from participations and net asset value
	const totalSavedWithParticipations = adhesionDetails.reduce((acc, item) => {
		const participations = item.numParticipations / 1000000;
		const netAssetValue = item.netAssetValue / 1000000000;
		return acc + participations * netAssetValue;
	}, 0);

	return totalSavedWithParticipations;
}

/**
 * Calculates total contributions with fallback for data inconsistencies
 * @param sumTotalContributions - Sum of total contributions in cents
 * @param sumCompanyContributions - Sum of company contributions in cents
 * @param sumUserContributions - Sum of user contributions in cents
 * @param totalSaved - Total saved amount
 * @returns Total contributions in euros
 */
export function calculateTotalContributions(
	sumTotalContributions: number,
	sumCompanyContributions: number,
	sumUserContributions: number,
	totalSaved: number
): number {
	const sumTotalContributionsFormatted = sumTotalContributions / 100;

	// Calculate fallback from individual contributions
	const { formattedCompanyContributions, formattedUserContributions } =
		formatArcaNumberCalculeCents(sumCompanyContributions, sumUserContributions);

	const formattedCompanyContributionsInNumber = Number(
		formattedCompanyContributions.replace(/\./g, '').replace(',', '.')
	);
	const formattedUserContributionsInNumber = Number(
		formattedUserContributions.replace(/\./g, '').replace(',', '.')
	);
	const fallbackTotalContributions =
		formattedCompanyContributionsInNumber + formattedUserContributionsInNumber;

	// Use sumTotalContributions if available
	let totalContributions =
		sumTotalContributionsFormatted > 0
			? sumTotalContributionsFormatted
			: fallbackTotalContributions;

	// Safety check: If total_contributions seems incorrect (multi-product bug), use fallback
	// Incorrect case: sum of total_contributions > sum of individual user+company contributions
	// This happens when total_contributions is duplicated across products
	if (sumTotalContributionsFormatted > fallbackTotalContributions * 1.5) {
		totalContributions = fallbackTotalContributions;
	}

	return totalContributions;
}

/**
 * Calculates performance from pension plan transfers
 * @param transfers - Array of pension plan transfers
 * @param totalSaved - Total saved amount
 * @returns Performance result with percentage and amount
 */
export function calculatePerformanceFromTransfers(
	transfers: PensionPlanTransfer[],
	totalSaved: number,
	planTypology?: PlanTypology
): PerformanceResult {
	if (!transfers || transfers.length === 0 || totalSaved === 0) {
		return { percentage: 0, amount: 0 };
	}

	const scoped = filterTransfersByPlanTypology(transfers, planTypology);

	// Sum all completed transfer amounts
	const totalTransferAmount = scoped
		.filter((transfer) => transfer.status === 'completed' && parseFloat(transfer.amount) > 0)
		.reduce((sum, transfer) => sum + parseFloat(transfer.amount), 0);

	if (totalTransferAmount === 0) {
		return { percentage: 0, amount: 0 };
	}

	// Calculate the difference. El % es sobre lo traspasado, lo que entró (product#47).
	const difference = totalSaved - totalTransferAmount;
	const percentage = (difference / totalTransferAmount) * 100;

	return { percentage, amount: difference };
}

/**
 * Calculates standard performance (difference between saved and contributions)
 * @param totalSaved - Total saved amount
 * @param totalContributions - Total contributions amount
 * @returns Performance result with percentage and amount
 */
export function calculateStandardPerformance(
	totalSaved: number,
	totalContributions: number
): PerformanceResult {
	if (totalSaved === 0 || totalContributions === 0) {
		return { percentage: 0, amount: 0 };
	}

	const amount = totalSaved - totalContributions;
	// Sobre lo aportado, no sobre lo que vale hoy (product#47): quien duplica ve +100 %, no +50 %.
	const percentage = (amount / totalContributions) * 100;

	return { percentage, amount };
}

/**
 * Determines if performance calculation is reliable
 * Pension plans typically have -50% to +30% annual returns in extreme scenarios
 * Anything beyond ±80% lifetime is suspicious
 * @param performancePercentage - Calculated performance percentage
 * @param totalSaved - Total saved amount
 * @param totalContributions - Total contributions amount
 * @param hasValidTransferData - Whether transfer-based calculation is being used
 * @returns True if performance is reliable
 */
export function isPerformanceReliable(
	performancePercentage: number,
	totalSaved: number,
	totalContributions: number,
	hasValidTransferData: boolean
): boolean {
	// Basic validation: totalSaved must be > 0
	if (totalSaved <= 0) {
		return false;
	}

	// Check if performance is within realistic bounds (-80% to 80%)
	const isWithinBounds = performancePercentage >= -80 && performancePercentage <= 80;

	if (hasValidTransferData) {
		// For transfer-based calculation, only check bounds and totalSaved
		return isWithinBounds;
	}

	// For standard calculation, also require valid contributions
	return isWithinBounds && totalContributions > 0;
}

/**
 * Calculates total transferred amount from completed transfers
 * @param transfers - Array of pension plan transfers
 * @returns Total transferred amount
 */
export function calculateTotalTransferredAmount(
	transfers: PensionPlanTransfer[],
	planTypology?: PlanTypology
): number {
	if (!transfers || transfers.length === 0) {
		return 0;
	}

	return filterTransfersByPlanTypology(transfers, planTypology)
		.filter((transfer) => transfer.status === 'completed' && parseFloat(transfer.amount) > 0)
		.reduce((sum, transfer) => sum + parseFloat(transfer.amount), 0);
}

/**
 * Determines final performance to use (transfer-based or standard)
 * For users with both transfers and contributions, combines both calculations
 * @param transferPerformance - Performance calculated from transfers
 * @param standardPerformance - Performance calculated from contributions
 * @param totalSaved - Total saved amount
 * @param totalTransferred - Total transferred amount
 * @param totalContributions - Total contributions amount
 * @returns Final performance result to use
 */
export function calculateFinalPerformance(
	transferPerformance: PerformanceResult,
	standardPerformance: PerformanceResult,
	totalSaved: number = 0,
	totalTransferred: number = 0,
	totalContributions: number = 0
): PerformanceResult {
	// Check if we have valid transfer data
	const hasValidTransferData =
		transferPerformance.percentage !== 0 || transferPerformance.amount !== 0;

	// Check if we have valid contributions data
	const hasValidContributionsData = totalContributions > 0;

	// Case 1: User has both transfers and contributions (e.g., company user who transferred external plan)
	// Calculate: totalSaved - totalTransferred - totalContributions
	if (hasValidTransferData && hasValidContributionsData && totalSaved > 0) {
		const amount = totalSaved - totalTransferred - totalContributions;
		// Sobre lo que entró: lo traspasado más lo aportado (product#47).
		const percentage = (amount / (totalTransferred + totalContributions)) * 100;
		return { percentage, amount };
	}

	// Case 2: User has only transfers (individual user, no company contributions)
	if (hasValidTransferData) {
		return transferPerformance;
	}

	// Case 3: User has only contributions (standard company user)
	return standardPerformance;
}

/**
 * Gets product by contributions (for company users)
 * Actions: userContributions === 0 && companyContributions === 0
 * Bonds: userContributions !== 0 || companyContributions !== 0
 */
function getProductByContributions(
	adhesionDetails: UserAdhesionDetailsI[],
	type: 'actions' | 'bonds'
): UserAdhesionDetailsI | undefined {
	if (!adhesionDetails || adhesionDetails.length === 0) {
		return undefined;
	}

	if (adhesionDetails.length === 1) {
		return type === 'actions' ? adhesionDetails[0] : undefined;
	}

	if (type === 'actions') {
		return adhesionDetails.find(
			(item) => item.userContributions === 0 && item.companyContributions === 0
		);
	} else {
		return adhesionDetails.find(
			(item) => item.userContributions !== 0 || item.companyContributions !== 0
		);
	}
}

/**
 * Calculates ROI (Return on Investment) for company users
 * Takes into account transfers if the user brought an external pension plan
 * @param totalSaved - Total saved amount
 * @param formattedUserContributions - User contributions in euros
 * @param formattedCompanyContributions - Company contributions in euros
 * @param totalTransferred - Total transferred from external plans in euros
 * @returns ROI percentage (can be Infinity if user contributions is 0)
 */
function calculateROI(
	totalSaved: number,
	formattedUserContributions: number,
	formattedCompanyContributions: number,
	totalTransferred: number = 0
): number {
	// Calculate total user investment (transfers + contributions)
	const totalUserInvestment = formattedUserContributions + totalTransferred;

	if (totalUserInvestment > 0) {
		return (totalSaved / totalUserInvestment - 1) * 100;
	} else if (totalUserInvestment === 0 && formattedCompanyContributions > 0) {
		return Infinity;
	} else {
		return 0;
	}
}

/**
 * UNIFIED FUNCTION - Calculates all pension plan metrics for both company and individual users
 * This function unifies the calculation logic from IndividualDashboardV2.tsx and MiBeneficioV2.tsx
 *
 * @param input - Configuration object with adhesion details, user type, and optional transfers
 * @returns Complete metrics result with all calculated values
 *
 * @example
 * // Company user
 * const metrics = calculatePensionPlanMetrics({
 *   adhesionDetails: companyAdhesionData,
 *   userType: UserType.COMPANY,
 *   transfers: []
 * });
 *
 * @example
 * // Individual user with transfers
 * const metrics = calculatePensionPlanMetrics({
 *   adhesionDetails: individualAdhesionData,
 *   userType: UserType.INDIVIDUAL,
 *   transfers: pensionPlanTransfers
 * });
 */
export function calculatePensionPlanMetrics(
	input: PensionPlanMetricsInput
): PensionPlanMetricsResult {
	const {
		adhesionDetails,
		userType,
		transfers = [],
		planTypology,
		externalTransferCostBasis = 0,
		totalRescued = 0
	} = input;

	// Step 1: Sum all adhesion detail fields
	const sumVestedRights = sumAdhesionDetailsField(adhesionDetails, 'vestedRights');
	const sumTotalContributions = sumAdhesionDetailsField(adhesionDetails, 'totalContributions');
	const sumCompanyContributions = sumAdhesionDetailsField(adhesionDetails, 'companyContributions');
	const sumUserContributions = sumAdhesionDetailsField(adhesionDetails, 'userContributions');

	// Step 2: Calculate total saved
	const totalSaved = calculateTotalSaved(adhesionDetails, sumVestedRights);

	// Step 3: Calculate total contributions with fallback
	const totalContributions = calculateTotalContributions(
		sumTotalContributions,
		sumCompanyContributions,
		sumUserContributions,
		totalSaved
	);

	// Step 4: Get formatted contributions in euros
	const { formattedCompanyContributions, formattedUserContributions } =
		formatArcaNumberCalculeCents(sumCompanyContributions, sumUserContributions);

	// Parse formatted contributions to numbers for UI display
	// The formatted strings always include thousand separators (dots) for both company and individual users,
	// so we need to remove them before parsing to numbers.
	const parseFormattedNumber = (formattedStr: string): number => {
		return Number(formattedStr.replace(/\./g, '').replace(',', '.'));
	};

	const formattedCompanyContributionsNum = parseFormattedNumber(formattedCompanyContributions);
	const formattedUserContributionsNum = parseFormattedNumber(formattedUserContributions);

	// Calculate real contributions in euros (for calculations, not for UI)
	// These values are NOT adjusted and represent the actual contributions
	const realUserContributionsEuros = sumUserContributions / 100;
	const realCompanyContributionsEuros = sumCompanyContributions / 100;

	// Step 5: Get products (actions/bonds) based on user type
	let actionsProduct: UserAdhesionDetailsI | undefined;
	let bondsProduct: UserAdhesionDetailsI | undefined;

	if (userType === UserType.COMPANY) {
		// Company users: Select by contributions
		actionsProduct = getProductByContributions(adhesionDetails, 'actions');
		bondsProduct = getProductByContributions(adhesionDetails, 'bonds');
	} else {
		// Individual users: Select by product code
		actionsProduct = getProductByCode(adhesionDetails, 31049);
		bondsProduct = getProductByCode(adhesionDetails, 31050);
	}

	// Step 6: Calculate total transferred amount (scoped al plan correspondiente)
	const totalTransferred = calculateTotalTransferredAmount(transfers, planTypology);

	// Step 7: Calculate performance. Lo rescatado vuelve al valor en TODOS los caminos: es lo que
	// salió, no una pérdida (product#47: (valor hoy + salido − entrado) / entrado).
	const valueWithRescued = totalSaved + totalRescued;
	const transferPerformance = calculatePerformanceFromTransfers(
		transfers,
		valueWithRescued,
		planTypology
	);
	const standardPerformance = calculateStandardPerformance(valueWithRescued, totalContributions);
	let performance = calculateFinalPerformance(
		transferPerformance,
		standardPerformance,
		valueWithRescued,
		totalTransferred,
		totalContributions
	);

	// Step 7b: Base de coste de traspasos externos (always-add). El back nos da
	// `externalTransferCostBasis` (Σ TRANSFER_EXTERNAL_INPUT) que NO está en
	// `daily.total_contributions`. Sumándolo a la base se corrige el rendimiento de:
	//  - traspaso puro (base 0 → +0,00%) → apreciación real del fondo
	//  - traspaso + aportaciones (inflado) → base real, % corregido a la baja
	// El rescate se suma de vuelta al valor (capital dispuesto pero generado).
	// Sin traspaso (externalTransferCostBasis = 0) → idéntico al histórico (sin regresión).
	const usesExternalCostBasis = externalTransferCostBasis > 0;
	// Lo que entró. Puede ser ≤ 0 aunque haya traspaso: las correcciones netean lo aportado.
	const effectiveCostBase = totalContributions + externalTransferCostBasis;
	if (usesExternalCostBasis) {
		const amount = valueWithRescued - effectiveCostBase;
		// Sobre lo que entró, no sobre el valor (product#47). Sin base, sin %: no fiable (abajo).
		performance = {
			amount,
			percentage: effectiveCostBase > 0 ? (amount / effectiveCostBase) * 100 : 0
		};
	}

	// Step 8: Validate performance reliability
	const hasValidTransferData =
		transferPerformance.percentage !== 0 ||
		transferPerformance.amount !== 0 ||
		usesExternalCostBasis;
	const performanceReliable =
		(!usesExternalCostBasis || effectiveCostBase > 0) &&
		isPerformanceReliable(performance.percentage, totalSaved, totalContributions, hasValidTransferData);

	// Step 9: Calculate ROI (mainly for company users)
	// Takes into account transfers to calculate accurate ROI
	// Uses REAL contributions (not adjusted), to calculate accurate ROI
	const roi = calculateROI(
		totalSaved,
		realUserContributionsEuros,
		realCompanyContributionsEuros,
		totalTransferred
	);

	// ROI NO calculable si el plan tiene traspaso externo: la "aportación propia" del
	// empleado ocurrió fuera de Arca y es desconocida. La UI muestra "Sin datos
	// suficientes" + (i). El rendimiento del fondo SÍ se calcula (no requiere ese dato).
	const roiIndeterminate = usesExternalCostBasis;

	// Step 10: Calculate total benefit in euros
	// This is what the user gained (company contributions + performance) excluding what they put in (their contributions + transfers)
	// Uses REAL contributions (not adjusted)
	const totalBenefitInEuros = totalSaved - realUserContributionsEuros - totalTransferred;

	return {
		// Basic metrics
		totalSaved,
		totalContributions,
		totalTransferred,

		// Contribution breakdown
		sumVestedRights,
		sumTotalContributions,
		sumCompanyContributions,
		sumUserContributions,
		formattedCompanyContributions: formattedCompanyContributionsNum,
		formattedUserContributions: formattedUserContributionsNum,

		// Products
		actionsProduct,
		bondsProduct,

		// Performance
		performance,
		isPerformanceReliable: performanceReliable,
		roiIndeterminate,

		// ROI and benefit
		roi,
		totalBenefitInEuros
	};
}
